// Agency View — the management console. Left-nav dashboard wiring the Phase 2–5
// sections: Help Desk, Plans, Analytics, Onboarding, and Settings.
// Themed (Console/Studio/HQ): one sidebar shell, tone driven entirely by --th-nav-* vars
// (HQ = dark gradient rail) — docs/2026-08-05-agency-console-themes.md
import { useEffect, useState } from 'react';
import type { Me } from '../lib/types';
import { agencyApi, setActiveAgencyId, setActiveAgentId } from '../lib/agencyApi';
import { applyTheme, cachedThemeId, getTheme, onThemeChange, type ThemeDefinition } from '../themes';
import { Icon, type IconName } from '../components/icons';
import HelpDesk from './agency/HelpDesk';
import Radar from './agency/Radar';
import WorkflowChanges from './agency/WorkflowChanges';
import PatchyHub from './agency/PatchyHub';
import Plans from './agency/Plans';
import Analytics from './agency/Analytics';
import TimeReport from './agency/TimeReport';
import SystemMap from './agency/SystemMap';
import SubAccounts from './agency/SubAccounts';
import Snapshots from './agency/Snapshots';
import Announcements from './agency/Announcements';
import Onboarding from './agency/Onboarding';
import Settings from './agency/Settings';
import ContentStudio from './agency/ContentStudio';
import Inbox from './agency/Inbox';
import ReviewCenter from './agency/ReviewCenter';

type Section =
  | 'helpdesk'
  | 'radar'
  | 'workflows'
  | 'plans'
  | 'analytics'
  | 'time'
  | 'systemmap'
  | 'subaccounts'
  | 'snapshots'
  | 'patchyhub'
  | 'announcements'
  | 'studio'
  | 'review'
  | 'inbox'
  | 'settings';

const NAV: { id: Section; label: string; icon: IconName }[] = [
  { id: 'helpdesk', label: 'Help Desk', icon: 'helpdesk' },
  { id: 'subaccounts', label: 'Sub-Accounts', icon: 'subaccounts' },
  { id: 'radar', label: 'Signals', icon: 'signals' },
  { id: 'workflows', label: 'Workflow Changes', icon: 'workflows' },
  { id: 'plans', label: 'Plans', icon: 'plans' },
  { id: 'analytics', label: 'Analytics', icon: 'analytics' },
  { id: 'time', label: 'Time', icon: 'time' },
  { id: 'systemmap', label: 'System Map', icon: 'systemmap' },
  { id: 'snapshots', label: 'Snapshots', icon: 'snapshots' },
  { id: 'patchyhub', label: 'PatchyHub', icon: 'patchyhub' },
  { id: 'announcements', label: 'Announcements', icon: 'announcements' },
  { id: 'studio', label: 'Content Studio', icon: 'studio' },
  { id: 'review', label: 'Review Center', icon: 'review' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

function greetingLine(hour: number, firstName?: string | null): string {
  const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  return firstName ? `${part}, ${firstName}` : part;
}

// Studio's spoken summary reuses the inbox unread count the console already loads — no new fetch.
function greetingSub(unread: number): string {
  if (unread === 0) return 'Nothing is waiting on you — everything is humming along.';
  if (unread === 1) return 'One update is waiting in your inbox.';
  return `${unread} updates are waiting in your inbox.`;
}

export default function AgencyView({ me }: { me: Me }) {
  // Scope every agency API call to the resolved agency (SSO in prod / ?as in dev) BEFORE any
  // child fetches — otherwise the Worker defaults to the demo agency and the console shows nothing.
  setActiveAgencyId(me.agencyId ?? null);
  setActiveAgentId(me.user.userId ?? null);
  const [active, setActive] = useState<Section>('helpdesk');
  // null = still loading; false = not yet onboarded (the gate owns the screen).
  const [onboarded, setOnboarded] = useState<boolean | null>(null);
  const [unread, setUnread] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  // Cached theme paints first (no flash); the saved setting reconciles when settings arrive.
  const [theme, setTheme] = useState<ThemeDefinition>(() => getTheme(cachedThemeId()));
  const current = NAV.find((n) => n.id === active) ?? { id: 'inbox' as Section, label: 'Inbox', icon: 'bell' as IconName };

  useEffect(() => {
    applyTheme(theme);
    return onThemeChange(setTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prime the bell's unread badge + the Review Center nav badge once on mount (the views keep them
  // in sync thereafter).
  useEffect(() => {
    agencyApi.inbox({ unread: true, limit: 1 }).then((r) => setUnread(r.unread)).catch(() => {});
    agencyApi.reviewCount().then((r) => setReviewCount(r.count)).catch(() => {});
    // The saved theme rides along on settings — apply it if it differs from the cached one.
    agencyApi.settings().then((s) => {
      if (s && getTheme(s.agency_theme).id !== cachedThemeId()) applyTheme(s.agency_theme);
    }).catch(() => {});
    // Decide up front whether this agency still needs setup. On failure assume onboarded — a
    // flaky call must not trap an established agency behind a wizard.
    agencyApi.onboarding()
      .then((o) => setOnboarded(o.completed_at != null))
      .catch(() => setOnboarded(true));
  }, []);

  // FIRST-RUN GATE — setup is the first thing an agency does, and then it gets out of the way.
  if (onboarded === false) {
    return (
      <div className="min-h-screen bg-canvas">
        <div className="mx-auto max-w-3xl px-6 py-10">
          <div className="mb-6 flex items-center gap-3">
            <img src="/logo.svg" alt="" className="h-9 w-9" />
            <div>
              <h1 className="font-display text-lg font-semibold text-ink">Let's get you set up</h1>
              <p className="text-sm text-muted">A few minutes now, and the console is ready to use.</p>
            </div>
          </div>
          <Onboarding onDone={() => setOnboarded(true)} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Sidebar — tone comes from --th-nav-* (HQ paints it as a dark gradient rail) */}
      <aside
        className="flex w-60 flex-col"
        style={{
          background: 'var(--th-nav-bg)',
          borderRight: '1px solid var(--th-nav-border)',
          color: 'var(--th-nav-ink)',
        }}
      >
        <div className="flex items-center gap-2.5 px-5 py-4">
          <img src="/logo.svg" alt="Support Specialist" className="h-8 w-8 shrink-0" />
          <div className="leading-tight">
            <div className="whitespace-nowrap font-display text-sm font-semibold" style={{ color: 'var(--th-nav-ink)' }}>
              Support Specialist
            </div>
            <div className="text-[11px]" style={{ color: 'var(--th-nav-muted)' }}>Agency Console</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {NAV.map((n) => {
            const isActive = active === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setActive(n.id)}
                className="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition"
                style={{
                  background: isActive ? 'var(--th-nav-active-bg)' : 'transparent',
                  color: isActive ? 'var(--th-nav-active-fg)' : 'var(--th-nav-muted)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'var(--th-nav-hover-bg)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name={n.icon} size={16} className="shrink-0 opacity-90" />
                <span className="flex-1">{n.label}</span>
                {n.id === 'review' && reviewCount > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-amber-500 px-1 text-[11px] font-semibold text-white">
                    {reviewCount > 99 ? '99+' : reviewCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3 text-xs" style={{ borderTop: '1px solid var(--th-nav-border)', color: 'var(--th-nav-muted)' }}>
          <div className="font-medium" style={{ color: 'var(--th-nav-ink)' }}>{me.user.name ?? 'Agency owner'}</div>
          <div className="truncate">{me.user.email ?? me.agencyId}</div>
          {/* Setup is a one-time gate, not a nav item. This is its only re-entry point. */}
          <button
            onClick={() => setOnboarded(false)}
            className="mt-2 text-[11px] underline underline-offset-2 transition hover:opacity-80"
            style={{ color: 'var(--th-nav-muted)' }}
          >
            Re-run setup
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b border-th-border bg-surface px-8 py-4">
          <h1 className="flex items-center gap-2.5 font-display text-lg font-semibold text-ink">
            <Icon name={current.icon} size={18} className="text-muted" />
            {current.label}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActive('inbox')}
              aria-label={`Inbox${unread ? `, ${unread} unread` : ''}`}
              className={`relative rounded-full p-2 text-muted transition hover:bg-canvas hover:text-ink ${
                active === 'inbox' ? 'bg-canvas text-ink' : ''
              }`}
            >
              <Icon name="bell" size={18} />
              {unread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
            <span className="rounded-full bg-canvas px-3 py-1 text-xs text-muted">
              Agency · {me.agencyId ?? 'demo'}
            </span>
          </div>
        </header>

        <div className="p-8">
          {active === 'helpdesk' && (
            <>
              {theme.layout.greeting && (
                <div className="mb-6">
                  <h1 className="font-display text-2xl font-semibold text-ink">
                    {greetingLine(new Date().getHours(), me.user.name?.split(' ')[0])}
                  </h1>
                  <p className="mt-1 text-sm font-medium text-muted">{greetingSub(unread)}</p>
                </div>
              )}
              <HelpDesk />
            </>
          )}
          {active === 'radar' && <Radar />}
          {active === 'workflows' && <WorkflowChanges />}
          {active === 'plans' && <Plans />}
          {active === 'analytics' && <Analytics />}
          {active === 'time' && <TimeReport />}
          {active === 'systemmap' && <SystemMap />}
          {active === 'subaccounts' && <SubAccounts />}
          {active === 'snapshots' && <Snapshots />}
          {active === 'patchyhub' && <PatchyHub />}
          {active === 'announcements' && <Announcements />}
          {active === 'studio' && <ContentStudio />}
          {active === 'review' && <ReviewCenter onCountChange={setReviewCount} />}
          {active === 'inbox' && <Inbox onUnreadChange={setUnread} />}
          {active === 'settings' && <Settings />}
        </div>
      </main>
    </div>
  );
}
